import { useState } from 'react';

const insights = [
  {
    category: 'Airspeed',
    icon: 'ri-speed-line',
    color: '#00FFD1',
    title: 'Airspeed fluctuation on final approach',
    detail: 'Your airspeed varied ±8 knots on final during 3 of 5 approaches today. Ideal variance is ±3 knots. Focus on trimming for Vref earlier on base leg.',
    priority: 'High',
    flight: 'Flight 8 — Apr 11',
  },
  {
    category: 'Radio',
    icon: 'ri-radio-line',
    color: '#00A3E0',
    title: 'Missing "traffic in sight" acknowledgment',
    detail: 'On two occasions you acknowledged traffic without confirming visual contact. Always include "traffic in sight" when ATC issues a traffic advisory.',
    priority: 'Medium',
    flight: 'Flight 8 — Apr 11',
  },
  {
    category: 'Pattern',
    icon: 'ri-flight-takeoff-line',
    color: '#B4FF39',
    title: 'Base leg altitude consistently low',
    detail: 'Your base leg altitude averaged 650 ft vs. the recommended 800 ft. This compresses your final approach and reduces correction time. Add power 200 ft earlier.',
    priority: 'High',
    flight: 'Flights 6, 7, 8',
  },
  {
    category: 'Workload',
    icon: 'ri-brain-line',
    color: '#00FFD1',
    title: 'Checklist timing improved significantly',
    detail: 'Your before-landing checklist is now completed consistently by mid-downwind — a major improvement from 3 flights ago. Keep this habit.',
    priority: 'Positive',
    flight: 'Flights 6–8',
  },
  {
    category: 'Maneuver',
    icon: 'ri-compass-3-line',
    color: '#00A3E0',
    title: 'Steep turns: bank angle holding well',
    detail: 'Bank angle held within ±3° throughout all steep turn maneuvers. Altitude loss reduced to 40 ft — within ACS standards. Excellent improvement.',
    priority: 'Positive',
    flight: 'Flight 7 — Apr 6',
  },
];

const priorityStyle: Record<string, string> = {
  High: 'bg-red-500/20 text-red-400 border-red-500/30',
  Medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  Positive: 'bg-[#B4FF39]/20 text-[#B4FF39] border-[#B4FF39]/30',
};

export default function FeatureCoachingInsights() {
  const [activeInsight, setActiveInsight] = useState<number>(0);

  return (
    <section id="coaching-insights" className="py-16 sm:py-24 bg-gradient-to-b from-[#0D1B2E] to-[#0A1628]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mb-12 sm:mb-16">
          <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-[#00FFD1]/10 border border-[#00FFD1]/30 rounded-2xl flex-shrink-0">
            <i className="ri-lightbulb-line text-2xl sm:text-3xl text-[#00FFD1]"></i>
          </div>
          <div>
            <div className="text-xs text-[#00FFD1] uppercase tracking-widest font-semibold mb-2">Capability 04</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4">Post-Flight Coaching Insights</h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-300 max-w-3xl leading-relaxed">
              After every flight, WingmanFix generates a personalized coaching report — specific, actionable, and grounded in your actual performance data. Not generic tips. Your flight. Your fixes.
            </p>
          </div>
        </div>

        <div className="flex flex-col lg:grid lg:grid-cols-5 gap-6 sm:gap-8">
          {/* Insight List */}
          <div className="lg:col-span-2 space-y-2 sm:space-y-3">
            {insights.map((ins, i) => (
              <button
                key={i}
                onClick={() => setActiveInsight(i)}
                className={`w-full text-left rounded-xl sm:rounded-2xl p-4 sm:p-5 border transition-all cursor-pointer ${
                  activeInsight === i
                    ? 'border-[#00FFD1]/50 bg-[#00FFD1]/5'
                    : 'border-gray-700 bg-[#0F1F35] hover:border-gray-500'
                }`}
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: `${ins.color}15` }}>
                    <i className={`${ins.icon} text-lg sm:text-xl`} style={{ color: ins.color }}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded border whitespace-nowrap ${priorityStyle[ins.priority]}`}>
                        {ins.priority}
                      </span>
                      <span className="text-xs text-gray-500">{ins.category}</span>
                    </div>
                    <p className="text-sm font-semibold text-white leading-snug">{ins.title}</p>
                    <p className="text-xs text-gray-500 mt-1">{ins.flight}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-3 flex flex-col gap-4 sm:gap-6">
            <div className="bg-[#050A14] rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-[#00FFD1]/20 shadow-2xl flex-1">
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: `${insights[activeInsight].color}15` }}>
                  <i className={`${insights[activeInsight].icon} text-xl sm:text-2xl`} style={{ color: insights[activeInsight].color }}></i>
                </div>
                <div>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded border whitespace-nowrap ${priorityStyle[insights[activeInsight].priority]}`}>
                    {insights[activeInsight].priority} Priority
                  </span>
                  <p className="text-xs text-gray-500 mt-1">{insights[activeInsight].flight}</p>
                </div>
              </div>

              <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">{insights[activeInsight].title}</h3>
              <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-6 sm:mb-8">{insights[activeInsight].detail}</p>

              <div className="bg-[#0A1628] rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-800">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 sm:mb-3">Recommended Drill</p>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-[#00FFD1] text-lg sm:text-xl mt-0.5 flex-shrink-0"></i>
                  <p className="text-sm text-gray-200 leading-relaxed">
                    Practice 5 solo touch-and-go circuits focusing specifically on this area. WingmanFix will track your improvement automatically on the next flight.
                  </p>
                </div>
              </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-3 sm:gap-4">
              {[
                { label: 'Insights Generated', value: '14', icon: 'ri-lightbulb-line', color: '#00FFD1' },
                { label: 'Issues Resolved', value: '9', icon: 'ri-checkbox-circle-line', color: '#B4FF39' },
                { label: 'Avg. Improvement', value: '+18%', icon: 'ri-line-chart-line', color: '#00A3E0' },
              ].map((s, i) => (
                <div key={i} className="bg-[#0F1F35] rounded-xl sm:rounded-2xl p-3 sm:p-5 border border-gray-700 text-center">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center mx-auto mb-1 sm:mb-2">
                    <i className={`${s.icon} text-xl sm:text-2xl`} style={{ color: s.color }}></i>
                  </div>
                  <p className="text-2xl sm:text-3xl font-bold text-white mb-1">{s.value}</p>
                  <p className="text-xs text-gray-500 uppercase tracking-wider leading-snug">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}